import { create } from "zustand"
import { immer } from "zustand/middleware/immer"
import {
    listJobs,
    type InternalJobApi,
    getInternalWorkspaces,
    type GetInternalJobsParams,
    type InternalWorkspaceUserApi,
    type GetInternalWorkspacesParams,
} from "@/src/services/internal"

import type { BaseStoreState, PaginationState } from "../types"

// ----------------------------------------------------------------------
// API response types (from internal/workspaces)
// ----------------------------------------------------------------------

export type InternalWorkspaceCredits = {
    isEnabled: boolean
    totalCredits: number
    addons?: Record<string, unknown>
}

export type InternalWorkspaceApi = {
    workspaceId: string
    workspaceName: string
    status: string
    websiteLink?: string
    aboutCompany?: string
    logoUrl?: string
    audioCredits?: InternalWorkspaceCredits
    videoCredits?: InternalWorkspaceCredits
    outboundAudioCredits?: InternalWorkspaceCredits
    /** Legacy/root flag; use `outboundAudioCredits.isEnabled` when `outboundAudioCredits` is present. */
    outboundAudioEnabled?: boolean
    generateCsvColumnCatalog?: boolean
    generateCsvColumnCatalogEnabled?: boolean
    interviewSchedulingEnabled?: boolean
    chargeAttemptRule?: string
    maxAttempts?: number
    createdAt?: string
    updatedAt?: string
    userCount: number
}

export type InternalWorkspacesResponseData = {
    totalElements: number
    totalPages: number
    first: boolean
    last: boolean
    size: number
    number: number
    content: InternalWorkspaceApi[]
    numberOfElements: number
    empty: boolean
}

// Table row shape used by Workspaces page and other consumers
export type InternalWorkspaceRow = {
    id: string
    name: string
    logoUrl?: string
    email: string
    userCount: number
    videoCreditsTotal: number
    audioCreditsTotal: number
    outboundAudioCreditsTotal: number
    status: "active" | "inactive"
    generateCsvColumnCatalogEnabled: boolean
    interviewSchedulingEnabled: boolean
}

function mapApiToWorkspaceRow(workspaceApi: InternalWorkspaceApi): InternalWorkspaceRow {
    const audioTotal = workspaceApi.audioCredits?.totalCredits ?? 0
    const videoTotal = workspaceApi.videoCredits?.totalCredits ?? 0
    const outboundAudioTotal = workspaceApi.outboundAudioCredits?.totalCredits ?? 0
    return {
        id: workspaceApi.workspaceId,
        name: workspaceApi.workspaceName,
        logoUrl: workspaceApi.logoUrl,
        email: "-",
        userCount: workspaceApi.userCount,
        videoCreditsTotal: videoTotal,
        audioCreditsTotal: audioTotal,
        outboundAudioCreditsTotal: outboundAudioTotal,
        status: workspaceApi.status === "ACTIVE" ? "active" : "inactive",
        generateCsvColumnCatalogEnabled:
            workspaceApi.generateCsvColumnCatalog ?? workspaceApi.generateCsvColumnCatalogEnabled ?? false,
        interviewSchedulingEnabled: workspaceApi.interviewSchedulingEnabled ?? false,
    }
}

// ----------------------------------------------------------------------
// Store
// ----------------------------------------------------------------------

export interface InternalStoreState extends BaseStoreState {
    workspaces: InternalWorkspaceRow[]
    /** Full workspace details from API (for Credits & VAS to read credits/pulse, etc.) */
    workspacesDetail: InternalWorkspaceApi[]
    pagination: PaginationState & { totalElements: number; totalPages: number }
    /** Workspace id of the row the user clicked (for create user, workspace users, link user APIs) */
    selectedWorkspaceId: string
    /** Jobs list from internal/jobs (Jobs & Leads page) */
    jobs: InternalJobApi[]
    jobsPagination: PaginationState & { totalElements: number; totalPages: number }
    jobsLoading: boolean
    jobsError: string | null
    /** Users already in the selected workspace (from getWorkspacesUsers), used by Link Existing User modal */
    workspaceUsers: InternalWorkspaceUserApi[]
    /** Job selected when user clicks a row on Jobs & Leads (navigates to job detail) */
    selectedJob: InternalJobApi | null
}

export interface InternalStoreActions {
    fetchWorkspaces: (params?: GetInternalWorkspacesParams) => Promise<void>
    fetchJobs: (params?: GetInternalJobsParams) => Promise<void>
    setSelectedWorkspaceId: (id: string) => void
    setSelectedJob: (job: InternalJobApi | null) => void
    setWorkspaceUsers: (users: InternalWorkspaceUserApi[]) => void
    setWorkspaceGenerateCsvColumnCatalogEnabled: (workspaceId: string, enabled: boolean) => void
    setWorkspaceInterviewSchedulingEnabled: (workspaceId: string, enabled: boolean) => void
}

export type InternalStore = InternalStoreState & InternalStoreActions

const defaultPagination = {
    page: 0,
    pageSize: 20,
    totalElements: 0,
    totalPages: 0,
}

export const useInternalStore = create<InternalStore>()(
    immer((set, get) => ({
        workspaces: [],
        workspacesDetail: [],
        pagination: defaultPagination,
        selectedWorkspaceId: "",
        jobs: [],
        jobsPagination: defaultPagination,
        jobsLoading: false,
        jobsError: null,
        workspaceUsers: [],
        selectedJob: null,
        loading: false,
        error: null,

        setSelectedWorkspaceId: (id: string) => {
            set((state) => {
                state.selectedWorkspaceId = id
            })
        },

        setSelectedJob: (job: InternalJobApi | null) => {
            set((state) => {
                state.selectedJob = job
            })
        },

        setWorkspaceUsers: (users: InternalWorkspaceUserApi[]) => {
            set((state) => {
                state.workspaceUsers = users
            })
        },

        setWorkspaceGenerateCsvColumnCatalogEnabled: (workspaceId: string, enabled: boolean) => {
            set((state) => {
                const workspaceRow = state.workspaces.find((workspace) => workspace.id === workspaceId)
                if (workspaceRow) {
                    workspaceRow.generateCsvColumnCatalogEnabled = enabled
                }
                const workspaceDetail = state.workspacesDetail.find(
                    (workspace) => workspace.workspaceId === workspaceId
                )
                if (workspaceDetail) {
                    workspaceDetail.generateCsvColumnCatalog = enabled
                    workspaceDetail.generateCsvColumnCatalogEnabled = enabled
                }
            })
        },

        setWorkspaceInterviewSchedulingEnabled: (workspaceId: string, enabled: boolean) => {
            set((state) => {
                const workspaceRow = state.workspaces.find((workspace) => workspace.id === workspaceId)
                if (workspaceRow) {
                    workspaceRow.interviewSchedulingEnabled = enabled
                }
                const workspaceDetail = state.workspacesDetail.find(
                    (workspace) => workspace.workspaceId === workspaceId
                )
                if (workspaceDetail) {
                    workspaceDetail.interviewSchedulingEnabled = enabled
                }
            })
        },

        fetchWorkspaces: async (params?: GetInternalWorkspacesParams) => {
            set((state) => {
                state.loading = true
                state.error = null
            })

            try {
                const response = await getInternalWorkspaces(params ?? { page: 0, size: 20 })
                const payload = response?.data as InternalWorkspacesResponseData | undefined
                const content = payload?.content ?? []
                const totalElements = payload?.totalElements ?? 0
                const totalPages = payload?.totalPages ?? 0
                const number = payload?.number ?? 0
                const size = payload?.size ?? 20

                set((state) => {
                    state.workspaces = content.map(mapApiToWorkspaceRow)
                    state.workspacesDetail = content
                    state.pagination = {
                        page: number,
                        pageSize: size,
                        totalElements,
                        totalPages,
                    }
                    state.loading = false
                    state.error = null
                })
            } catch (error) {
                set((state) => {
                    state.loading = false
                    state.error = error instanceof Error ? error.message : "Failed to fetch workspaces"
                })
            }
        },

        fetchJobs: async (params?: GetInternalJobsParams) => {
            set((state) => {
                state.jobsLoading = true
                state.jobsError = null
            })
            try {
                const response = await listJobs(params ?? { page: 0, size: 20 })
                const payload = response?.data
                const content = payload?.content ?? []
                const totalElements = payload?.totalElements ?? 0
                const totalPages = payload?.totalPages ?? 0
                const number = payload?.number ?? 0
                const size = payload?.size ?? 20
                set((state) => {
                    state.jobs = content
                    state.jobsPagination = {
                        page: number,
                        pageSize: size,
                        totalElements,
                        totalPages,
                    }
                    state.jobsLoading = false
                    state.jobsError = null
                })
            } catch (error) {
                set((state) => {
                    state.jobsLoading = false
                    state.jobsError = error instanceof Error ? error.message : "Failed to fetch jobs"
                })
            }
        },
    }))
)
